import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import * as XLSX from "xlsx";
import { moduleActivityApi, UserAPi } from "../../common/api";
import { getDefaultColDef } from "../../common/constants/commonFunction";
import { layoutStyle } from "../layout-ui/style";
import { TruckLoader } from "./truckLoader";

export const UsageReport = () => {
  const classes = layoutStyle();
  const defaultColDef = useMemo(() => getDefaultColDef(), []);
  const [activityRows, setActivityRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [userLoadError, setUserLoadError] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [anchorElExport, setAnchorElExport] = useState(null);

  useEffect(() => {
    let isCurrentRequest = true;

    const loadUsers = async () => {
      const response = await UserAPi.getUser({
        limit: 10000,
        offset: 0,
        filter: {},
        sorting: {
          fieldName: "",
          direction: "",
        },
      });

      if (!isCurrentRequest) return;
      if (response?.error) {
        setUserLoadError("User list could not be loaded.");
        return;
      }

      setUsers(response?.data?.data || []);
    };

    loadUsers().catch((error) => {
      console.error("Unable to load users for module activity filter.", error);
      if (isCurrentRequest) {
        setUserLoadError("User list could not be loaded.");
      }
    });

    return () => {
      isCurrentRequest = false;
    };
  }, []);

  useEffect(() => {
    if (startDate && endDate && startDate > endDate) {
      setLoading(false);
      return;
    }

    let isCurrentRequest = true;
    const loadUsageReport = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const request = {
          startDate: startDate || null,
          endDate: endDate || null,
          filterUserId: filterUserId ? Number(filterUserId) : null,
        };
        const activityResponse =
          await moduleActivityApi.getUsageReport(request);
        if (isCurrentRequest) {
          setActivityRows(activityResponse?.data?.data || []);
          if (activityResponse?.error) {
            setLoadError("Module activity data could not be loaded.");
          }
        }
      } finally {
        if (isCurrentRequest) {
          setLoading(false);
        }
      }
    };

    loadUsageReport();

    return () => {
      isCurrentRequest = false;
    };
  }, [endDate, filterUserId, startDate]);

  const formatDuration = (milliseconds) => {
    const totalSeconds = Math.floor((milliseconds || 0) / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}:${String(seconds).padStart(2, "0")}`;
  };

  const formatActivityDate = (value) =>
    value ? moment.utc(value).format("MM/DD/YYYY") : "";

  const formatActivityTime = (value) =>
    value ? moment.utc(value).format("hh:mm:ss A") : "";

  const handleExportFile = (format) => {
    const exportData = activityRows.map((row) => ({
      User: row.userName || "",
      // "User ID": row.userId,
      Module: row.moduleName || "",
      Activity: row.activityTitle || "",
      "Starting Date": row.startingTime
        ? formatActivityDate(row.startingTime)
        : "",
      "Starting Time": row.startingTime
        ? formatActivityTime(row.startingTime)
        : "",
      "Exit Date": row.exitTime ? formatActivityDate(row.exitTime) : "",
      "Exit Time": row.exitTime ? formatActivityTime(row.exitTime) : "",
      "Total Time": formatDuration(row.totalTimeMilliseconds),
      "Idle Time": formatDuration(row.idleTime),
      "Active Time": formatDuration(row.activeTimeMilliseconds),
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Module Activity");
    XLSX.writeFile(workbook, `ModuleActivityReport.${format}`);
    setAnchorElExport(null);
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setFilterUserId("");
  };

  return (
    <Box className={classes.mainContentCompany}>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Box className={classes.headerNameText}>Usage Report</Box>
        <Box className={classes.addEditGrid}>
          <Button
            variant="outlined"
            className={[classes.fleetListNewButton, classes.fontSize12]}
            onClick={(event) => setAnchorElExport(event.currentTarget)}
            disabled={loading || !activityRows.length}
          >
            Export
          </Button>
          <Menu
            anchorEl={anchorElExport}
            open={Boolean(anchorElExport)}
            onClose={() => setAnchorElExport(null)}
          >
            <MenuItem onClick={() => handleExportFile("xlsx")}>
              Export XLSX
            </MenuItem>
            <MenuItem onClick={() => handleExportFile("csv")}>
              Export CSV
            </MenuItem>
          </Menu>
        </Box>
      </Stack>
      {/* <Tabs
        value={0}
        sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0 } }}
      >
         <Tab label="Report Usage" /> 
        <Tab label="Module Activity" />
      </Tabs>*/}
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          flexWrap: "wrap",
          gap: "8px",
          mt: "6px",
          mb: "4px",
          padding: "6px 10px",
          backgroundColor: "#f5f7f5",
          borderRadius: "6px",
          border: "1px solid #dde8da",
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: "#18333A",
            minWidth: "max-content",
          }}
        >
          Timeframe:
        </Typography>
        <TextField
          type="date"
          size="small"
          label="Start Date"
          InputLabelProps={{ shrink: true }}
          inputProps={{ placeholder: "MM/DD/YYYY" }}
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
          sx={{
            "& .MuiInputBase-input": { fontSize: "12px", padding: "6px 10px", textTransform: "uppercase" },
            "& .MuiInputLabel-root": { fontSize: "12px" },
            "& .MuiOutlinedInput-root": { borderRadius: "4px" },
            minWidth: 150,
          }}
        />
        <Typography sx={{ fontSize: "12px", color: "#555" }}>to</Typography>
        <TextField
          type="date"
          size="small"
          label="End Date"
          InputLabelProps={{ shrink: true }}
          inputProps={{ placeholder: "MM/DD/YYYY" }}
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
          sx={{
            "& .MuiInputBase-input": { fontSize: "12px", padding: "6px 10px", textTransform: "uppercase" },
            "& .MuiInputLabel-root": { fontSize: "12px" },
            "& .MuiOutlinedInput-root": { borderRadius: "4px" },
            minWidth: 150,
          }}
        />
        <TextField
          select
          size="small"
          label="User"
          value={filterUserId}
          onChange={(event) => setFilterUserId(event.target.value)}
          sx={{
            "& .MuiInputBase-input": { fontSize: "12px", padding: "6px 10px" },
            "& .MuiInputLabel-root": { fontSize: "12px" },
            "& .MuiOutlinedInput-root": { borderRadius: "4px" },
            minWidth: 120,
          }}
        >
          <MenuItem value="">All users</MenuItem>
          {users.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {user.userName || user.username}
            </MenuItem>
          ))}
        </TextField>
        {(startDate || endDate || filterUserId) && (
          <Button
            size="small"
            variant="outlined"
            onClick={handleClearFilters}
            sx={{
              fontSize: "11px",
              textTransform: "none",
              color: "error.main",
              borderColor: "error.main",
              padding: "3px 10px",
            }}
          >
            Clear
          </Button>
        )}
        {(startDate || endDate || filterUserId) && (
          <Typography
            sx={{
              fontSize: "11px",
              color: "#18333A",
              fontStyle: "italic",
              ml: 1,
            }}
          >
            {`Showing ${activityRows.length} record(s)`}
          </Typography>
        )}
      </Stack>
      {loadError && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {loadError}
        </Alert>
      )}
      {userLoadError && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {userLoadError}
        </Alert>
      )}
      <Box className={classes.gridContantDealer}>
        <Box
          sx={{
            height: "calc(100vh - 220px)",
            minHeight: 300,
            width: "100%",
          }}
        >
          {loading ? (
            <TruckLoader />
          ) : (
            <AgGridReact
              className="ag-theme-quartz"
              rowSelection="multiple"
              pagination={true}
              rowData={activityRows}
              defaultColDef={defaultColDef}
              columnDefs={[
                // { field: "userId", headerName: "User ID" },
                { field: "userName", headerName: "User" },
                { field: "moduleName", headerName: "Module / Page" },
                { field: "activityTitle", headerName: "Activity" },
                ...[
                  ["startingTime", "Starting Date", formatActivityDate],
                  ["startingTime", "Starting Time", formatActivityTime],
                  ["exitTime", "Exit Date", formatActivityDate],
                  ["exitTime", "Exit Time", formatActivityTime],
                ].map(([field, headerName, formatter]) => ({
                  field,
                  headerName,
                  valueFormatter: (params) => formatter(params?.value),
                })),
                {
                  field: "totalTimeMilliseconds",
                  headerName: "Total Time",
                  valueFormatter: (params) => formatDuration(params?.value),
                },
                {
                  field: "idleTime",
                  headerName: "Idle Time",
                  valueFormatter: (params) => formatDuration(params?.value),
                },
                {
                  field: "activeTimeMilliseconds",
                  headerName: "Active Time",
                  valueFormatter: (params) => formatDuration(params?.value),
                },
              ]}
              suppressRowClickSelection={true}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
